// Pure helpers behind the SNP picker: what a registry variant already belongs to, what may be posted
// to the attach verb, and how a delimited file becomes a bulk-import records array.
import { Staging } from "../../../../types/geneCodes/StagingTypes";
import { RegistryVariant } from "../../../../types/geneCodes/RegistryTypes";

// PanelController::attachSnps validates exactly these and drops everything else server-side.
const ATTACH_KEYS = ["rs", "risk_allele", "subcategory", "client", "rank", "gene", "taqman", "var_alias", "notes", "bene_allele", "quality", "GxE_pos", "GxE_neg", "mod_type", "trait_desc", "trait_searcher"] as const;

// StagingController::bulkImport mass-assigns the record, so only real staging columns go in it.
const IMPORT_KEYS = [
	"category",
	"subcategory",
	"client",
	"rank",
	"subcat_x",
	"gene",
	"rs",
	"taqman",
	"var_alias",
	"notes",
	"risk_allele",
	"bene_allele",
	"endophenotype",
	"searcher",
	"searcher_mod",
	"GxE_pos",
	"GxE_neg",
	"epistasis",
	"quality",
	"actionable",
	"modifiable",
	"marker_type",
	"array_type",
	"hz_coef",
] as const;

const NUMERIC_KEYS = new Set(["rank", "quality", "modifiable", "hz_coef"]);

export interface SnpCandidate {
	rs: string;
	// R7: the row IS a registry doc — the picker searches the registry, not the wheels already built.
	doc: RegistryVariant;
	// Every wheel/trait this variant is in, annotated from staging; membership is no longer the source.
	memberships: string[];
	// The (rs, this panel, target trait) triple already exists, so the server would skip this row.
	alreadyHere: boolean;
}

interface RegistryAssociation {
	risk_allele?: unknown;
}

/** The reader-facing marker; the registry `id` is its lowercased twin. */
export const displayRs = (doc: RegistryVariant): string => doc.rsid_display || doc.id || "";

// associations_json rides the doc verbatim from the gateway but is not on the picker's own type.
const associationsOf = (doc: RegistryVariant): RegistryAssociation[] => {
	const raw = (doc as unknown as { associations_json?: unknown }).associations_json;
	return Array.isArray(raw) ? (raw.filter((item) => typeof item === "object" && item !== null) as RegistryAssociation[]) : [];
};

/** The registry's OWN risk allele, and only when every association agrees — a contested or absent one is left off. */
export const registryRiskAllele = (doc: RegistryVariant): string | null => {
	const alleles = Array.from(
		new Set(
			associationsOf(doc)
				.map((association) => (typeof association.risk_allele === "string" ? association.risk_allele.trim() : ""))
				.filter((allele) => allele !== ""),
		),
	);

	return alleles.length === 1 ? alleles[0] : null;
};

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === "";

const isRegistryDoc = (row: Staging | RegistryVariant): row is RegistryVariant => "rsid_display" in row || "gene_symbols" in row;

// Two spellings of "no trait" — removeSnpsFromTrait blanks subcategory rather than nulling it.
const sameTrait = (left: string | null | undefined, right: string | null | undefined): boolean => (isBlank(left) ? isBlank(right) : left === right);

/**
 * One candidate per distinct registry variant, annotated with the wheels it already sits in.
 *
 * Membership is matched with BOTH sides folded, which is what the server's case-insensitive
 * collation does: staging.rs is ~80% uppercase and the registry id is lowercase, so a verbatim
 * compare would annotate almost nothing. The server still reports what it skipped and stays the
 * authority on duplicates — this is the affordance, not the guard.
 */
export const buildCandidates = (docs: RegistryVariant[], panelName: string | null, panelSnps: Staging[], targetTrait: string | null, catalogueRows: Staging[] = []): SnpCandidate[] => {
	const candidates = new Map<string, SnpCandidate>();

	docs.forEach((doc) => {
		const rs = displayRs(doc);
		const key = rs.toLowerCase();
		if (rs === "" || candidates.has(key)) return;

		const candidate: SnpCandidate = { rs, doc, memberships: [], alreadyHere: false };

		// Every staging row for this variant, in this wheel, whatever trait it is under.
		panelSnps
			.filter((snp) => (snp.rs ?? "").toLowerCase() === key)
			.forEach((snp) => {
				if (sameTrait(snp.subcategory, targetTrait)) {
					candidate.alreadyHere = true;
					return;
				}
				const label = `${panelName} › ${snp.subcategory}`;
				if (!candidate.memberships.includes(label)) candidate.memberships.push(label);
			});

		// The other wheels, off the catalogue rows read alongside the search.
		catalogueRows
			.filter((row) => (row.rs ?? "").toLowerCase() === key)
			.forEach((row) => {
				if (row.category && row.category !== panelName && !candidate.memberships.includes(row.category)) {
					candidate.memberships.push(row.category);
				}
			});

		candidates.set(key, candidate);
	});

	return Array.from(candidates.values());
};

/**
 * The attach payload for one candidate, placed in `subcategory`. Takes a registry doc or a Staging row.
 */
export const toAttachPayload = (row: Staging | RegistryVariant, subcategory: string | null): Record<string, unknown> => {
	if (isRegistryDoc(row)) {
		const gene = (row.gene_symbols ?? [])[0];
		const riskAllele = registryRiskAllele(row);

		return {
			rs: displayRs(row),
			...(isBlank(gene) ? {} : { gene }),
			// R7: the server takes risk_allele as nullable, so an absence is sent as an absence, never invented.
			...(riskAllele === null ? {} : { risk_allele: riskAllele }),
			subcategory,
		};
	}

	const payload: Record<string, unknown> = {};

	ATTACH_KEYS.forEach((key) => {
		const value = (row as unknown as Record<string, unknown>)[key];
		if (isBlank(value)) return;
		// `mod_type` is validated `nullable|json`, so a decoded object would 422 the whole batch.
		if (key === "mod_type" && typeof value !== "string") return;
		payload[key] = value;
	});

	payload.subcategory = subcategory;
	return payload;
};

/**
 * Split delimited text into cells. Comma or tab, RFC 4180 quoting, CR/LF tolerant.
 */
export const parseDelimited = (text: string): string[][] => {
	const body = text.replace(/^﻿/, "");
	const firstLine = body.split(/\r?\n/, 1)[0] ?? "";
	const delimiter = firstLine.includes("\t") && !firstLine.includes(",") ? "\t" : ",";

	const rows: string[][] = [];
	let row: string[] = [];
	let cell = "";
	let quoted = false;

	for (let index = 0; index < body.length; index += 1) {
		const char = body[index];

		if (quoted) {
			if (char !== '"') {
				cell += char;
			} else if (body[index + 1] === '"') {
				cell += '"';
				index += 1;
			} else {
				quoted = false;
			}
			continue;
		}

		if (char === '"') {
			quoted = true;
		} else if (char === delimiter) {
			row.push(cell);
			cell = "";
		} else if (char === "\n" || char === "\r") {
			if (char === "\r" && body[index + 1] === "\n") index += 1;
			row.push(cell);
			rows.push(row);
			row = [];
			cell = "";
		} else {
			cell += char;
		}
	}

	if (cell !== "" || row.length > 0) {
		row.push(cell);
		rows.push(row);
	}

	return rows.filter((cells) => cells.some((value) => value.trim() !== ""));
};

// Header names are matched loosely; VALUES never are, least of all `rs`.
const headerKey = (header: string): string | null => {
	const normalised = header.trim().toLowerCase().replace(/[\s-]+/g, "_");
	return IMPORT_KEYS.find((key) => key.toLowerCase() === normalised) ?? null;
};

export interface ImportDefaults {
	category: string;
	subcategory: string | null;
}

/**
 * A delimited file as a bulk-import records array. A row's own category/subcategory wins; the wheel
 * the picker is open on fills in for the common file that carries neither.
 */
export const toImportRecords = (text: string, defaults: ImportDefaults): Partial<Staging>[] => {
	const [header, ...lines] = parseDelimited(text);
	if (!header) return [];

	const keys = header.map(headerKey);
	if (!keys.includes("rs")) return [];

	return lines.map((cells) => {
		const record: Record<string, unknown> = { category: defaults.category, subcategory: defaults.subcategory };

		keys.forEach((key, column) => {
			if (!key) return;
			const value = (cells[column] ?? "").trim();
			if (value === "") return;
			record[key] = NUMERIC_KEYS.has(key) && !Number.isNaN(Number(value)) ? Number(value) : value;
		});

		return record as Partial<Staging>;
	});
};
